import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, RefreshCw, Loader2, Plus, X, Zap, AlertTriangle,
  BarChart3, Filter, Sparkles, Info,
} from 'lucide-react';
import { adminGet, adminPost } from '../data/api';

const mono = { fontFamily: 'JetBrains Mono, monospace' };
const heading = { fontFamily: 'Outfit, sans-serif' };
const card = { background: '#18181B', border: '1px solid #27272A' };

const OPS = ['>=', '<=', '>', '<', '==', '!='];

function fmtNum(v, dec = 3) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return Number(v).toFixed(dec);
}
function fmtPct(v, dec = 1) {
  if (v === null || v === undefined || Number.isNaN(Number(v))) return '—';
  return `${(Number(v) * 100).toFixed(dec)}%`;
}

// Colore cella in base al lift rispetto alla baseline
function liftColor(rate, baseline) {
  if (rate == null || baseline == null) return '#3F3F46';
  const d = rate - baseline;
  if (d > 0.08) return '#10B981';
  if (d > 0.03) return '#34D399';
  if (d > 0.01) return '#6EE7B7';
  if (d < -0.08) return '#EF4444';
  if (d < -0.03) return '#F87171';
  if (d < -0.01) return '#FCA5A5';
  return '#52525B';
}

function Tab({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-2 px-4 py-2 text-xs rounded-sm transition-colors ${
        active
          ? 'bg-emerald-700 text-white border border-emerald-600'
          : 'bg-[#18181B] text-zinc-400 border border-[#27272A] hover:text-zinc-200'
      }`}
      style={heading}
    >
      <Icon size={12} /> {label}
    </button>
  );
}

// -------------------- UNIVARIATE TAB --------------------
function UnivariateTab({ schema }) {
  const [feature, setFeature] = useState('p1_sets_diff_recent');
  const [outcome, setOutcome] = useState('p1_won');
  const [bins, setBins] = useState(8);
  const [scope, setScope] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminGet(
        `/tt/admin/feature-explorer/univariate?feature=${feature}&outcome=${outcome}&bins=${bins}&scope=${scope}`
      );
      setData(r);
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const isBool = schema?.outcomes?.find(o => o.key === outcome)?.kind === 'bool';
  const baseline = isBool ? data?.overall?.rate : data?.overall?.mean;
  const maxN = useMemo(
    () => Math.max(1, ...(data?.bins || []).map(b => b.n || 0)),
    [data]
  );

  return (
    <div className="space-y-4" data-testid="tab-univariate">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs" style={mono}>
        <select
          data-testid="univ-feature-select"
          value={feature}
          onChange={e => setFeature(e.target.value)}
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        >
          {schema?.features?.map(f =>
            <option key={f.key} value={f.key}>{f.label}{f.derived ? ' (derivata)' : ''}</option>
          )}
        </select>
        <select
          data-testid="univ-outcome-select"
          value={outcome}
          onChange={e => setOutcome(e.target.value)}
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        >
          {schema?.outcomes?.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <select
          data-testid="univ-bins-select"
          value={bins}
          onChange={e => setBins(Number(e.target.value))}
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        >
          {[4, 6, 8, 10, 12, 15, 20].map(b => <option key={b} value={b}>{b} bin</option>)}
        </select>
        <select
          data-testid="univ-scope-select"
          value={scope}
          onChange={e => setScope(e.target.value)}
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        >
          {schema?.scopes?.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <button
          onClick={run}
          disabled={loading}
          data-testid="univ-run-btn"
          className="flex items-center justify-center gap-1 bg-emerald-700 hover:bg-emerald-600 rounded-sm text-white px-3 py-1.5 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <BarChart3 size={12} />}
          Calcola
        </button>
      </div>

      {err && (
        <div className="rounded-sm p-3 text-xs text-red-300 flex items-center gap-2" data-testid="univ-error"
          style={{ background: '#2D1416', border: '1px solid #4B1F22' }}>
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {data && (
        <div className="rounded-sm p-4" style={card} data-testid="univ-results">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-wider text-zinc-500" style={heading}>
              Distribuzione outcome per bucket
            </div>
            <div className="text-xs text-zinc-500" style={mono}>
              Baseline {isBool ? fmtPct(baseline) : fmtNum(baseline)} · n={data?.overall?.n || 0}
            </div>
          </div>

          <div className="space-y-1" style={mono}>
            {(data.bins || []).map((b, i) => {
              const rate = isBool ? b.rate : b.mean;
              const barPct = rate != null ? rate * 100 : 0;
              const widthN = (b.n / maxN) * 100;
              return (
                <div key={i} data-testid={`univ-bin-${i}`} className="flex items-center gap-2">
                  <div className="w-40 text-xs text-zinc-400 truncate">{b.label}</div>
                  <div className="flex-1 relative h-6 bg-[#0D0D0F] rounded-sm overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0 rounded-sm"
                      style={{
                        width: `${widthN}%`,
                        background: liftColor(rate, baseline),
                        opacity: 0.4,
                      }}
                    />
                    {isBool && baseline != null && (
                      <div
                        className="absolute inset-y-0 border-l border-dashed border-zinc-500 opacity-60"
                        style={{ left: `${baseline * 100}%` }}
                        title={`baseline ${fmtPct(baseline)}`}
                      />
                    )}
                    <div className="absolute inset-0 flex items-center px-2 text-xs text-zinc-100 font-semibold gap-2">
                      <span>{isBool ? fmtPct(rate) : fmtNum(rate)}</span>
                      <span className="text-zinc-500 text-[11px]">
                        ({b.n} match, CI {isBool ? fmtPct(b.ci_low) : fmtNum(b.ci_low)}–{isBool ? fmtPct(b.ci_high) : fmtNum(b.ci_high)})
                      </span>
                      {rate != null && baseline != null && (
                        <span
                          className="ml-auto text-[11px]"
                          style={{ color: liftColor(rate, baseline) }}
                        >
                          {rate > baseline ? '▲' : '▼'} {((rate - baseline) * 100).toFixed(1)}pp
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-16 text-right text-xs text-zinc-500">n={b.n}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-4 text-[11px] text-zinc-600 flex items-center gap-1" style={mono}>
            <Info size={11} /> Larghezza barra = numerosità del bucket · colore = scostamento dalla baseline · linea tratteggiata = baseline · CI 95% Wilson.
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- CUSTOM QUERY TAB --------------------
function CustomQueryTab({ schema }) {
  const [conditions, setConditions] = useState([
    { feature: 'p1_sets_diff_recent', op: '>=', value: '1.5' },
    { feature: 'p1_last5_winrate',    op: '>=', value: '0.8' },
  ]);
  const [outcome, setOutcome] = useState('p1_won');
  const [scope, setScope] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const addCond = () => setConditions([
    ...conditions,
    { feature: schema?.features?.[0]?.key || 'p1_last5_winrate', op: '>=', value: '0.5' },
  ]);
  const rmCond = (i) => setConditions(conditions.filter((_, idx) => idx !== i));
  const setCond = (i, patch) =>
    setConditions(conditions.map((c, idx) => idx === i ? { ...c, ...patch } : c));

  const run = async () => {
    setLoading(true); setErr(null);
    try {
      const payload = {
        conditions: conditions.map(c => ({ ...c, value: Number(c.value) })),
        outcome, scope,
      };
      const r = await adminPost('/tt/admin/feature-explorer/custom-query', undefined, payload);
      setData(r);
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setLoading(false); }
  };

  const isBool = schema?.outcomes?.find(o => o.key === outcome)?.kind === 'bool';

  return (
    <div className="space-y-4" data-testid="tab-custom">
      <div className="rounded-sm p-4 space-y-2" style={card}>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2" style={heading}>
          Condizioni (AND)
        </div>
        {conditions.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-2 items-center" style={mono} data-testid={`cond-row-${i}`}>
            <select
              value={c.feature}
              onChange={e => setCond(i, { feature: e.target.value })}
              data-testid={`cond-feature-${i}`}
              className="col-span-6 bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200 text-xs"
            >
              {schema?.features?.map(f =>
                <option key={f.key} value={f.key}>{f.label}</option>
              )}
            </select>
            <select
              value={c.op}
              onChange={e => setCond(i, { op: e.target.value })}
              data-testid={`cond-op-${i}`}
              className="col-span-2 bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200 text-xs"
            >
              {OPS.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
            <input
              type="number" step="any"
              value={c.value}
              onChange={e => setCond(i, { value: e.target.value })}
              data-testid={`cond-value-${i}`}
              className="col-span-3 bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200 text-xs"
            />
            <button
              onClick={() => rmCond(i)}
              data-testid={`cond-remove-${i}`}
              className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-center"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <div className="flex items-center justify-between pt-2">
          <button
            onClick={addCond}
            data-testid="cond-add-btn"
            className="flex items-center gap-1 text-xs text-zinc-400 hover:text-emerald-300"
            style={mono}
          >
            <Plus size={12} /> Aggiungi condizione
          </button>
          <div className="flex items-center gap-2">
            <select
              value={outcome}
              onChange={e => setOutcome(e.target.value)}
              data-testid="cq-outcome-select"
              className="bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200 text-xs"
            >
              {schema?.outcomes?.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
            <select
              value={scope}
              onChange={e => setScope(e.target.value)}
              data-testid="cq-scope-select"
              className="bg-[#0D0D0F] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200 text-xs"
            >
              {schema?.scopes?.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <button
              onClick={run}
              disabled={loading}
              data-testid="cq-run-btn"
              className="flex items-center gap-1 bg-emerald-700 hover:bg-emerald-600 rounded-sm text-white px-3 py-1.5 text-xs disabled:opacity-50"
            >
              {loading ? <Loader2 size={12} className="animate-spin" /> : <Filter size={12} />}
              Calcola
            </button>
          </div>
        </div>
      </div>

      {err && (
        <div className="rounded-sm p-3 text-xs text-red-300 flex items-center gap-2"
          data-testid="cq-error"
          style={{ background: '#2D1416', border: '1px solid #4B1F22' }}>
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {data && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cq-results">
          <div className="rounded-sm p-4" style={card}>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1" style={heading}>
              Match filtrati
            </div>
            <div className="text-3xl font-bold text-emerald-300" style={mono}>
              {isBool ? fmtPct(data.filtered?.rate) : fmtNum(data.filtered?.mean)}
            </div>
            <div className="text-xs text-zinc-500 mt-1" style={mono}>
              n={data.filtered?.n} · CI [{isBool ? fmtPct(data.filtered?.ci_low) : fmtNum(data.filtered?.ci_low)}, {isBool ? fmtPct(data.filtered?.ci_high) : fmtNum(data.filtered?.ci_high)}]
            </div>
          </div>
          <div className="rounded-sm p-4" style={card}>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1" style={heading}>
              Baseline (tutto lo scope)
            </div>
            <div className="text-3xl font-bold text-zinc-300" style={mono}>
              {isBool ? fmtPct(data.baseline?.rate) : fmtNum(data.baseline?.mean)}
            </div>
            <div className="text-xs text-zinc-500 mt-1" style={mono}>
              n={data.baseline?.n}
            </div>
          </div>
          <div className="rounded-sm p-4" style={card}>
            <div className="text-xs uppercase tracking-wider text-zinc-500 mb-1" style={heading}>
              Lift
            </div>
            <div
              className="text-3xl font-bold"
              style={{
                ...mono,
                color: data.lift != null
                  ? (data.lift > 0 ? '#34D399' : data.lift < 0 ? '#F87171' : '#A1A1AA')
                  : '#A1A1AA',
              }}
            >
              {data.lift != null ? `${data.lift > 0 ? '+' : ''}${(data.lift * 100).toFixed(2)}pp` : '—'}
            </div>
            <div className="text-xs text-zinc-500 mt-1" style={mono}>
              vs baseline
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// -------------------- TOP PATTERNS TAB --------------------
function TopPatternsTab({ schema }) {
  const [outcome, setOutcome] = useState('p1_won');
  const [scope, setScope] = useState('all');
  const [minSupport, setMinSupport] = useState(200);
  const [topN, setTopN] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  const run = async () => {
    setLoading(true); setErr(null);
    try {
      const r = await adminGet(
        `/tt/admin/feature-explorer/top-patterns?outcome=${outcome}&scope=${scope}&min_support=${minSupport}&top_n=${topN}`
      );
      setData(r);
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);

  const isBool = schema?.outcomes?.find(o => o.key === outcome)?.kind === 'bool';

  const renderTable = (rows, title, testid) => (
    <div className="rounded-sm" style={card} data-testid={testid}>
      <div className="px-4 py-3 border-b border-[#27272A] text-xs uppercase tracking-wider text-zinc-500" style={heading}>
        {title}
      </div>
      <table className="w-full text-xs" style={mono}>
        <thead>
          <tr className="text-zinc-500 uppercase text-[10px]">
            <th className="text-left px-3 py-2 font-medium">Regola</th>
            <th className="text-right px-3 py-2 font-medium">n</th>
            <th className="text-right px-3 py-2 font-medium">{isBool ? 'WR' : 'Mean'}</th>
            <th className="text-right px-3 py-2 font-medium">Lift</th>
            <th className="text-right px-3 py-2 font-medium">CI 95%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={i} className="border-t border-[#27272A]/60 text-zinc-300 hover:bg-[#1F1F22]"
                data-testid={`${testid}-row-${i}`}>
              <td className="px-3 py-2">{p.rule}</td>
              <td className="text-right px-3 py-2 text-zinc-500">{p.n}</td>
              <td className="text-right px-3 py-2 text-zinc-100">
                {isBool ? fmtPct(p.metric) : fmtNum(p.metric)}
              </td>
              <td
                className="text-right px-3 py-2 font-semibold"
                style={{ color: p.lift > 0 ? '#34D399' : p.lift < 0 ? '#F87171' : '#A1A1AA' }}
              >
                {p.lift > 0 ? '+' : ''}{(p.lift * 100).toFixed(2)}pp
              </td>
              <td className="text-right px-3 py-2 text-zinc-500 text-[11px]">
                [{isBool ? fmtPct(p.ci_low, 0) : fmtNum(p.ci_low)}, {isBool ? fmtPct(p.ci_high, 0) : fmtNum(p.ci_high)}]
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={5} className="text-center text-zinc-500 py-8">Nessuna regola trovata.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="tab-top">
      <div className="grid grid-cols-1 md:grid-cols-5 gap-2 text-xs" style={mono}>
        <select
          value={outcome}
          onChange={e => setOutcome(e.target.value)}
          data-testid="tp-outcome-select"
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        >
          {schema?.outcomes?.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <select
          value={scope}
          onChange={e => setScope(e.target.value)}
          data-testid="tp-scope-select"
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        >
          {schema?.scopes?.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <input
          type="number" min="10" max="5000" step="10"
          value={minSupport}
          onChange={e => setMinSupport(Number(e.target.value) || 100)}
          placeholder="min support"
          data-testid="tp-min-support"
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        />
        <select
          value={topN}
          onChange={e => setTopN(Number(e.target.value))}
          data-testid="tp-topn-select"
          className="bg-[#18181B] border border-[#27272A] rounded-sm px-2 py-1.5 text-zinc-200"
        >
          {[10, 15, 20, 30, 50].map(n => <option key={n} value={n}>Top {n}</option>)}
        </select>
        <button
          onClick={run}
          disabled={loading}
          data-testid="tp-run-btn"
          className="flex items-center justify-center gap-1 bg-emerald-700 hover:bg-emerald-600 rounded-sm text-white px-3 py-1.5 disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
          Scopri
        </button>
      </div>

      {err && (
        <div className="rounded-sm p-3 text-xs text-red-300 flex items-center gap-2"
          data-testid="tp-error"
          style={{ background: '#2D1416', border: '1px solid #4B1F22' }}>
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {data && (
        <>
          <div className="text-xs text-zinc-500" style={mono}>
            Baseline: <b>{isBool ? fmtPct(data.baseline) : fmtNum(data.baseline)}</b> · {data.evaluated} regole valutate
          </div>
          {renderTable(data.top_positive || [], `Top ${topN} pattern POSITIVI (lift > baseline)`, 'tp-positive')}
          {renderTable(data.top_negative || [], `Top ${topN} pattern NEGATIVI (lift < baseline)`, 'tp-negative')}
        </>
      )}
    </div>
  );
}

// -------------------- MAIN PAGE --------------------
export default function AdminFeatureExplorer() {
  const [tab, setTab] = useState('univariate');
  const [schema, setSchema] = useState(null);
  const [info, setInfo] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [err, setErr] = useState(null);

  const loadMeta = async () => {
    try {
      const [s, i] = await Promise.all([
        adminGet('/tt/admin/feature-explorer/schema'),
        adminGet('/tt/admin/feature-explorer/dataset-info'),
      ]);
      setSchema(s); setInfo(i);
    } catch (e) { setErr(String(e?.message || e)); }
  };

  useEffect(() => { loadMeta(); }, []);

  const rebuild = async () => {
    setRebuilding(true);
    try {
      const r = await adminPost('/tt/admin/feature-explorer/rebuild', undefined, {});
      setInfo(r);
    } catch (e) { setErr(String(e?.message || e)); }
    finally { setRebuilding(false); }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0F] text-zinc-200 px-4 py-6 max-w-[1400px] mx-auto"
         data-testid="admin-feature-explorer-page">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Link to="/admin" className="text-zinc-500 hover:text-zinc-300" data-testid="back-to-admin">
            <ArrowLeft size={18} />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-zinc-100" style={heading}>Feature Explorer</h1>
            <p className="text-xs text-zinc-500">
              Pattern discovery su feature calcolate × outcome reali (11k+ match)
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {info && (
            <div className="text-[11px] text-zinc-500" style={mono} data-testid="dataset-meta">
              {info.rows_count} match · cache {info.from_cache ? 'hit' : 'fresh'} · età {Math.floor(info.age_sec / 60)}m
            </div>
          )}
          <button
            onClick={rebuild}
            disabled={rebuilding}
            data-testid="rebuild-dataset-btn"
            className="flex items-center gap-1 bg-[#18181B] border border-[#27272A] rounded-sm text-xs px-3 py-1.5 hover:bg-[#27272A] disabled:opacity-50"
          >
            {rebuilding ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Rigenera dataset
          </button>
        </div>
      </div>

      {err && (
        <div className="rounded-sm p-3 text-xs text-red-300 flex items-center gap-2 mb-4"
          data-testid="page-error"
          style={{ background: '#2D1416', border: '1px solid #4B1F22' }}>
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      <div className="flex items-center gap-2 mb-4">
        <Tab active={tab === 'univariate'} onClick={() => setTab('univariate')}
             icon={BarChart3} label="Univariate" testid="tab-btn-univariate" />
        <Tab active={tab === 'custom'} onClick={() => setTab('custom')}
             icon={Filter} label="Custom Query" testid="tab-btn-custom" />
        <Tab active={tab === 'top'} onClick={() => setTab('top')}
             icon={Sparkles} label="Top Patterns" testid="tab-btn-top" />
      </div>

      {!schema && !err && (
        <div className="text-xs text-zinc-500 flex items-center gap-2" style={mono}>
          <Loader2 size={12} className="animate-spin" /> Caricamento schema…
        </div>
      )}

      {schema && tab === 'univariate' && <UnivariateTab schema={schema} />}
      {schema && tab === 'custom' && <CustomQueryTab schema={schema} />}
      {schema && tab === 'top' && <TopPatternsTab schema={schema} />}
    </div>
  );
}
